import { LitElement, html, Splide } from '@hh/global.js'

class ProductCard extends LitElement {
  static properties = {}

  constructor() {
    super()
  }

  connectedCallback() {
    super.connectedCallback()
    this.initializeSplide()
    this.handleQuickBuy()
    this.checkVIPDiscount()
    this.handleLinkClick()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
  }

  initializeSplide() {
    const carouselEl = this.querySelector('.js-product-card-splide')
    if (!carouselEl) return

    this.productCardSplide = new Splide(carouselEl, {
      arrows: false,
      pagination: true,
      useScroll: true,
      mediaQuery: 'min',
      type: 'fade',
      cloneStatus: false,
      rewind: true,
      start: 0,
      breakpoints: {
        768: {
          pagination: false,
          arrows: true,
          type: 'fade',
          cloneStatus: false,
          rewind: true,
          start: 0,
        },
      },
    }).mount()
  }

  handleQuickBuy() {
    const quickBuyButtonEl = this.querySelectorAll(
      '.js-product-card-quickbuy-button'
    )
    const quickBuyEl = this.querySelector('.js-product-card-quickbuy')

    quickBuyButtonEl?.forEach((buttonEl) => {
      buttonEl.addEventListener('click', (e) => {
        quickBuyEl?.classList.add('is-active')
        quickBuyEl?.setAttribute('open', true)
      })
    })
  }

  checkVIPDiscount() {
    const cookies = Object.fromEntries(
      document.cookie
        .split('; ')
        .map((v) => v.split(/=(.*)/s).map(decodeURIComponent))
    )

    const vipPromoEL = this.querySelectorAll('.js-product-card-vip-promo')

    vipPromoEL.forEach((el) => {
      if (!('discount_code' in cookies)) {
        el.classList.remove('hidden')
      }
    })
  }

  handleLinkClick() {
    const link = this.querySelector('[data-product-card-link]')

    link?.addEventListener('click', (e) => {
      if (!e.target.closest('a')) return

      const linkHasPageReference = e.target.closest(`[data-page]`)
      if (!linkHasPageReference) return true

      e.preventDefault()

      const updatedURL = new URL(window.location.href)

      if (linkHasPageReference.hasAttribute('id')) {
        updatedURL.hash = linkHasPageReference.getAttribute('id')
      }

      updatedURL.searchParams.set('page', linkHasPageReference.dataset.page)

      history.pushState({}, '', updatedURL.toString())

      window.location.href = linkHasPageReference.getAttribute('href')
    })
  }

  render() {
    return html`<slot></slot>`
  }
}

customElements.define('product-card', ProductCard)

class ProductQuickBuy extends LitElement {
  static properties = {
    open: {
      state: false,
      type: Boolean,
    },
    bisEnabled: {
      type: Boolean,
      attribute: 'bis-enabled',
    },
  }

  constructor() {
    super()

    const quickBuyCloseButton = this.querySelector(
      '.js-product-card-quickbuy-close'
    )
    const quickBuyOverlay = this.querySelector(
      '.js-product-card-quickbuy-overlay'
    )

    quickBuyCloseButton?.addEventListener('click', () => {
      this.handleCloseQuickBuy()
    })

    quickBuyOverlay?.addEventListener('click', () => {
      this.handleCloseQuickBuy()
    })

    this.labelAddtobag = this.dataset.labelAddtobag
    this.labelOutofstock = this.dataset.labelOutofstock
    this.labelNotifyMe = this.dataset.labelNotifyme || ''
    this.submitButton = this?.querySelector('.js-product-card-quickbuy-submit')
    this.productFromEl = this?.querySelector('form')
    this._updateSubmitButton = this._updateSubmitButton.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()
    this.handleVariantChange()
    this.handleProductAdded()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
  }

  isMobile() {
    return !window.matchMedia('(min-width: 768px)').matches
  }

  updated() {
    if (this.open && this.isMobile()) {
      document.body.classList.add('overflow-hidden')
    } else if (!this.open && this.isMobile()) {
      document.body.classList.remove('overflow-hidden')
    }

    this.handleSplideOverlay()
  }

  previousSplidePos = null

  handleSplideOverlay() {
    const splideListEl = this.closest('.splide__list')

    if (!this.isMobile() || !splideListEl) return

    if (this.open) {
      this.previousSplidePos = splideListEl.style.transform
      document.body.classList.add('quickbuy-open')
    } else {
      document.body.classList.remove('quickbuy-open')
      splideListEl.style.transform = this.previousSplidePos
    }
  }

  handleVariantChange() {
    const variantItemEl = this.querySelectorAll(
      'input[type="radio"][name="id"]'
    )
    const learnMoreEl = this.querySelector(
      '.js-product-card-quickbuy-learn-more'
    )

    const resetHiddenFields = () => {
      const hiddenFields = this.querySelectorAll('input[type="hidden"]') || []

      hiddenFields.forEach((item) => {
        item.setAttribute('disabled', '')
      })
    }

    variantItemEl?.forEach((item) => {
      item.addEventListener('change', (e) => {
        resetHiddenFields()
        learnMoreEl?.setAttribute('href', e.target.dataset?.variantUrl)
        const hiddenFields = item
          .closest('li')
          ?.querySelectorAll('input[type="hidden"]')
        hiddenFields.forEach((item) => {
          item.removeAttribute('disabled')
        })
        this._updateSubmitButton(item)
      })
    })

    if (!!variantItemEl) {
      this._updateSubmitButton(variantItemEl[0])
    }
  }

  _updateSubmitButton(item) {
    if (!item) return

    const variantItemWrapper = item.closest('.product-card-quickbuy__list-item')

    if (
      variantItemWrapper.classList.contains(
        'product-card-quickbuy__list-item--sold-out'
      )
    ) {
      if (this.bisEnabled) {
        this.submitButton.classList.add('js-product-bis-trigger')
        this.submitButton.setAttribute(
          'data-product-handle',
          item.dataset?.variantHandle
        )
        this.submitButton.setAttribute('type', 'button')
        this.submitButton.innerHTML = this.labelNotifyMe
      } else {
        this.submitButton.setAttribute('disabled', true)
        this.submitButton.innerHTML = this.labelOutofstock
      }
    } else {
      this.submitButton.classList.remove('js-product-bis-trigger')
      this.submitButton.removeAttribute('disabled')
      this.submitButton.setAttribute('type', 'submit')
      this.submitButton.innerHTML = this.labelAddtobag
    }
  }

  handleProductAdded() {
    this.addEventListener('productAdded', () => {
      if (!this.isMobile()) return

      this.handleCloseQuickBuy()
    })
  }

  handleEscape(e) {
    e.code.toUpperCase() === 'ESCAPE' && this.handleCloseQuickBuy()
  }

  handleCloseQuickBuy() {
    this?.classList.remove('is-active')
    this?.removeAttribute('open')
    this?.productFromEl?.reset()
  }

  render() {
    return html`<hh-focus-trap active="${!!this.open}">
      <slot @keyup="${this.handleEscape}"></slot>
    </hh-focus-trap>`
  }
}

customElements.define('product-card-quickbuy', ProductQuickBuy)
